/**
 * Clinic setup — one destination, tabbed (frozen decision: never five
 * navigation entries). Tabs are permission-shaped: absent, not disabled.
 */

import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import a from '../access/access.module.css';
import { TabBar } from '@/ui/formkit';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM, type PermissionKey } from '@/core/authz/permissions';

/**
 * ZOY-377: the ONE surface → permission map. Both the tab bar (visibility)
 * and the routes (direct-URL guards, app/router.tsx) read it, so navigation
 * and route access cannot drift apart: a tab a role cannot see is also a
 * URL it cannot open.
 */
export const SETUP_SURFACES = {
  profile: PERM.settingsRead,
  staff: PERM.membersManage,
  roles: PERM.rolesManage,
  hours: PERM.availabilityRead,
  rooms: PERM.roomsRead,
  services: PERM.catalogRead,
  catalogs: PERM.catalogRead,
  // ZOY-776: extra-charge kinds live with clinic settings.
  extraCharges: PERM.settingsRead,
  templates: PERM.packagesRead,
} as const satisfies Record<string, PermissionKey>;

const TABS: readonly { id: keyof typeof SETUP_SURFACES; path: string; labelId: string }[] = [
  { id: 'profile', path: '/clinic-setup', labelId: 'setup.tabProfile' },
  { id: 'staff', path: '/clinic-setup/staff', labelId: 'setup.tabStaff' },
  { id: 'roles', path: '/clinic-setup/roles', labelId: 'setup.tabRoles' },
  { id: 'hours', path: '/clinic-setup/hours', labelId: 'setup.tabHours' },
  { id: 'rooms', path: '/clinic-setup/rooms', labelId: 'setup.tabRooms' },
  { id: 'services', path: '/clinic-setup/services', labelId: 'setup.tabServices' },
  { id: 'catalogs', path: '/clinic-setup/catalogs', labelId: 'setup.tabCatalogs' },
  { id: 'extraCharges', path: '/clinic-setup/extra-charges', labelId: 'setup.tabExtraCharges' },
  { id: 'templates', path: '/clinic-setup/templates', labelId: 'tpl.tab' },
];

export function SetupLayout() {
  const intl = useIntl();
  const authz = useAuthz();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (st) => st.location.pathname });

  const visible = TABS.filter((t) => authz.can(SETUP_SURFACES[t.id]));
  const active =
    [...visible].reverse().find((t) => pathname.startsWith(t.path))?.id ??
    visible[0]?.id ??
    'profile';

  return (
    <div>
      <div className={a.pageHead}>
        <h1 className={a.pageTitle}>{intl.formatMessage({ id: 'setup.title' })}</h1>
      </div>
      <TabBar
        tabs={visible.map((t) => ({ id: t.id, label: intl.formatMessage({ id: t.labelId }) }))}
        active={active}
        onPick={(id) => {
          const tab = visible.find((t) => t.id === id);
          if (tab) void navigate({ to: tab.path });
        }}
      />
      <Outlet />
    </div>
  );
}
